import { action, computed, observable } from 'mobx';
import { Container, Inject } from 'typescript-ioc';
import { HubModuleType, IHubModulesBatchDTO, IRemovedHubModuleDTO, TCreateHubModuleDTO, THubModuleDTO, } from '../../../../../../../api/event-hubs.dao';
import { IAccountContactDTO } from '../../../../../../../api/account-contacts.dao';
import { EventAttributeTypesValues, IAttributesDAO, IEventAttributeDetailsDTO } from '../../../../../../../api/attributes.dao';
import { SortOrder } from '../../../../../../../api/shared.dto';
import { AttributesDAO } from '../../../../../../../api/implementations/attributes.dao';
import { IRouter } from '../../../../../../../infrastructure/router';
import { RouteParams } from '../../../../../../../routes-table';
import { RADIX_PARAM } from '../../../../../../../constants/common.constants';
import { IErrorParsing } from '../../../../../../../infrastructure/error-parsing';
import { EventName, IEventDispatcher } from '../../../../../../../infrastructure/event-dispatcher';
import { alertServerError } from '../../../../../../global/modal/alerts-host/alert-templates';
import { errorText } from '../../../../../../../constants/common-labels.constants';
import { HubModuleVM } from './hub-module.viewmodel';
import { createEmptyModule, createModuleFromDTO, IHubModuleOptions } from './hub-module.factory';

export class ModulesVM {
    @observable
    private modules: HubModuleVM[] = [];

    @observable
    private removedModuleIds: number[] = [];

    @observable
    private editingModuleVM: HubModuleVM | null = null;

    @observable
    private isAddingNewModule = false;

    @observable
    private moduleTypePickerOpen = false;

    private initialModuleIds: Set<number> = new Set();
    private initialModulesData: THubModuleDTO[] = [];
    private eventAttributeOptions: IEventAttributeDetailsDTO[] = [];

    constructor(
        @Inject private readonly attributesDAO: IAttributesDAO = Container.get(AttributesDAO),
        @Inject private readonly routingService: IRouter<RouteParams> = Container.get(IRouter),
        @Inject private readonly errorParsingService: IErrorParsing = Container.get(IErrorParsing),
        @Inject private readonly eventDispatcher: IEventDispatcher = Container.get(IEventDispatcher),
    ) {
        this.eventDispatcher.registerHandler(EventName.SpeakerUpdated, this.handleSpeakerUpdated);
    }

    public dispose(): void {
        this.modules.forEach(module => module.dispose());
        this.editingModuleVM?.dispose();
        this.eventDispatcher.unregisterHandler(EventName.SpeakerUpdated, this.handleSpeakerUpdated);
    }

    public getModules = (): HubModuleVM[] => {
        return [...this.modules];
    };

    @computed
    public get activeModules(): HubModuleVM[] {
        return this.modules.filter(m => m.isEnabled());
    };

    @computed
    public get inactiveModules(): HubModuleVM[] {
        return this.modules.filter(m => !m.isEnabled());
    };

    public getEditingModuleVM = (): HubModuleVM | null => {
        return this.editingModuleVM;
    };

    public getEventAttributeOptions = (): IEventAttributeDetailsDTO[] => {
        return this.eventAttributeOptions;
    };

    @action
    public setModules = (modules: THubModuleDTO[]): void => {
        const sorted = [...modules].sort((a, b) => a.positionOrder - b.positionOrder);
        const moduleIds = sorted.map(m => m.moduleId);
        this.initialModulesData = sorted;
        this.initialModuleIds = new Set(moduleIds);
        this.removedModuleIds = [];
        this.loadModulesFromDTOs(sorted);
    };

    @action
    public setEventAttributeOptions = (eventAttributeOptions: IEventAttributeDetailsDTO[]): void => {
        this.eventAttributeOptions = eventAttributeOptions;
    };

    public isAddingModule = (): boolean => {
        return this.isAddingNewModule;
    };

    public isModuleTypePickerOpen = (): boolean => {
        return this.moduleTypePickerOpen;
    };

    public areAllModulesValid = (): boolean => {
        return this.modules.every(m => m.isValid());
    };

    public touchAllInvalidModules = (): void => {
        this.modules.forEach(m => {
            if (!m.isValid()) {
                m.markTouched();
            }
        });
    };

    public hasModuleChanges = (): boolean => {
        const hasRemovals = this.removedModuleIds.length > 0;
        const hasEdits = this.modules.some(m => m.hasUnsavedChanges());
        return hasRemovals || hasEdits;
    };

    public buildModulesBatch = (): IHubModulesBatchDTO => {
        return {
            addedModules: this.getAddedModules(),
            updatedModules: this.getUpdatedModules(),
            removedModules: this.getRemovedModules(),
        };
    };

    @action
    public discardChanges = (): void => {
        this.removedModuleIds = [];
        this.loadModulesFromDTOs(this.initialModulesData);
        this.closeModal();
        this.moduleTypePickerOpen = false;
    };

    @action
    public openModuleTypePicker = (): void => {
        this.moduleTypePickerOpen = true;
    };

    @action
    public closeModuleTypePicker = (): void => {
        this.moduleTypePickerOpen = false;
    };

    @action
    public addModule = (type: HubModuleType): void => {
        const positionOrder = this.activeModules.length;
        this.moduleTypePickerOpen = false;
        this.editingModuleVM = createEmptyModule(type, positionOrder, this.moduleOptions());
        this.isAddingNewModule = true;
    };

    @action
    public openEditModuleModal = (rowVM: HubModuleVM): void => {
        this.editingModuleVM = rowVM.clone();
        this.isAddingNewModule = false;
    };

    @action
    public confirmEditingModule = (): void => {
        if (!this.editingModuleVM || !this.editingModuleVM.isValid()) {
            this.editingModuleVM?.markTouched();
            return;
        }
        if (this.isAddingNewModule) {
            this.placeModuleInSection(this.editingModuleVM);
        } else {
            this.applyEditedModule(this.editingModuleVM);
        }
        this.closeModal();
    };

    @action
    public cancelEditingModule = (): void => {
        this.editingModuleVM?.dispose();
        this.closeModal();
    };

    @action
    public reorderActiveModules = (orderedActive: HubModuleVM[]): void => {
        const inactiveModules = this.inactiveModules;
        const orderedModules = [...orderedActive, ...inactiveModules];
        this.setOrderedModules(orderedModules);
    };

    @action
    public toggleModuleActive = (vm: HubModuleVM): void => {
        vm.setEnabled(!vm.isEnabled());
        this.placeModuleInSection(vm);
    };

    @action
    public removeModule = (vm: HubModuleVM): void => {
        const remainingModules = this.modules.filter(m => m !== vm);
        this.setOrderedModules(remainingModules);
        this.trackRemovalIfPersisted(vm);
        vm.dispose();
    };

    public loadEventAttributeOptions = async (): Promise<void> => {
        const accountId = parseInt(this.routingService.router.getState().params.accountId, RADIX_PARAM);

        try {
            const result = await this.attributesDAO.getEventAttributesList({
                accountId: accountId,
                sortField: 'createdAt',
                sortOrder: SortOrder.Desc,
                inputTypes: [
                    EventAttributeTypesValues.SINGLE_SELECT_DROPDOWN,
                    EventAttributeTypesValues.MULTI_SELECT_DROPDOWN,
                    EventAttributeTypesValues.CHECKBOX,
                ],
            });
            this.setEventAttributeOptions(result.content);
        } catch (error) {
            const message = this.errorParsingService.extractMessage(error);
            alertServerError(errorText(), message);
        }
    };

    private loadModulesFromDTOs(dtos: THubModuleDTO[]): void {
        this.modules.forEach(module => module.dispose());
        const moduleViewModels = dtos.map(dto => createModuleFromDTO(dto, this.moduleOptions()));
        this.modules = this.partitionByState(moduleViewModels);
    }

    private handleSpeakerUpdated = (contact: IAccountContactDTO): void => {
        this.initialModulesData = this.initialModulesData.map(moduleDTO => {
            if (moduleDTO.moduleType !== HubModuleType.SPEAKERS) {
                return moduleDTO;
            }
            return {
                ...moduleDTO,
                speakers: moduleDTO.speakers.map(speaker =>
                    speaker.accountContactId === contact.accountContactId
                        ? { ...contact, positionOrder: speaker.positionOrder }
                        : speaker
                ),
            };
        });
    };

    @action
    private applyEditedModule(editedVM: HubModuleVM): void {
        const idx = this.modules.findIndex(m => m.getClientId() === editedVM.getClientId());
        if (idx < 0) {
            console.error(`Cannot apply edited module: no module matches client id ${editedVM.getClientId()}`);
            return;
        }
        const oldVM = this.modules[idx];
        const enabledStateChanged = oldVM.isEnabled() !== editedVM.isEnabled();
        if (enabledStateChanged) {
            this.placeModuleInSection(editedVM);
        } else {
            this.modules[idx] = editedVM;
        }
        oldVM.dispose();
    }

    @action
    private placeModuleInSection(moduleToPlace: HubModuleVM): void {
        const others = this.modules.filter(m => m.getClientId() !== moduleToPlace.getClientId());
        const orderedModules = this.insertByState(others, moduleToPlace);
        this.setOrderedModules(orderedModules);
    }

    @action
    private setOrderedModules(modules: HubModuleVM[]): void {
        this.modules = modules;
        this.modules.forEach((m, i) => m.updatePositionOrder(i));
    }

    private partitionByState(modules: HubModuleVM[]): HubModuleVM[] {
        const active = modules.filter(m => m.isEnabled());
        const inactive = modules.filter(m => !m.isEnabled());
        return [...active, ...inactive];
    }

    private insertByState(others: HubModuleVM[], moduleToPlace: HubModuleVM): HubModuleVM[] {
        const active = others.filter(m => m.isEnabled());
        const inactive = others.filter(m => !m.isEnabled());
        return moduleToPlace.isEnabled()
            ? [...active, moduleToPlace, ...inactive]
            : [...active, ...inactive, moduleToPlace];
    }

    private trackRemovalIfPersisted(vm: HubModuleVM): void {
        const moduleId = vm.getModuleId();
        if (moduleId === null) {
            return;
        }
        const wasPersisted = this.initialModuleIds.has(moduleId);
        if (wasPersisted) {
            this.removedModuleIds.push(moduleId);
        }
    }

    @action
    private closeModal(): void {
        this.editingModuleVM = null;
        this.isAddingNewModule = false;
    }

    private moduleOptions(): IHubModuleOptions {
        return { eventAttributeOptions: this.eventAttributeOptions };
    }

    private getAddedModules(): TCreateHubModuleDTO[] {
        const newModules = this.modules.filter(module => module.isNew());
        return newModules.map(module => module.toCreateDTO());
    }

    private getUpdatedModules(): THubModuleDTO[] {
        const changedModules = this.modules.filter(module => !module.isNew() && module.hasUnsavedChanges());
        return changedModules.map(module => module.toUpdateDTO());
    }

    private getRemovedModules(): IRemovedHubModuleDTO[] {
        return this.removedModuleIds.map(moduleId => ({ moduleId: moduleId }));
    }
}
